import React from 'react';

import FormField from '@amzn/awsui-components-console/form-field';
import DateRangePicker, {
  DateRangePickerProps,
} from '@amzn/awsui-components-console/date-range-picker';

import {
  isValidRange,
  dateDisabledReason,
  isDateEnabled,
  RELATIVE_RANGE_MESSAGE_IDS,
} from 'src/common/utils/dateFormatters';
import { useTranslation, type TranslateFn } from 'src/i18n/shim/useTranslation';

/** Presets shown in the relative range selector. */
const RELATIVE_PRESETS: DateRangePickerProps.RelativeOption[] = [
  { key: 'last-7-days', amount: 7, unit: 'day', type: 'relative' },
  { key: 'last-30-days', amount: 30, unit: 'day', type: 'relative' },
  { key: 'last-90-days', amount: 90, unit: 'day', type: 'relative' },
  { key: 'last-6-months', amount: 6, unit: 'month', type: 'relative' },
];

/** The relative-range phrases live in `dateFormatters` because the saved-filter label renders them
 * too; these unit names are only needed here. */
const UNIT_IDS: Record<DateRangePickerProps.TimeUnit, string> = {
  second: 'date-range-unit-second',
  minute: 'date-range-unit-minute',
  hour: 'date-range-unit-hour',
  day: 'date-range-unit-day',
  week: 'date-range-unit-week',
  month: 'date-range-unit-month',
  year: 'date-range-unit-year',
};

/** i18n strings for the DateRangePicker. */
const dateRangeI18nStrings = (
  t: TranslateFn,
): DateRangePickerProps.I18nStrings => ({
  todayAriaLabel: t('date-range-today-aria'),
  nextMonthAriaLabel: t('date-range-next-month-aria'),
  previousMonthAriaLabel: t('date-range-previous-month-aria'),
  customRelativeRangeDurationLabel: t('date-range-duration-label'),
  customRelativeRangeDurationPlaceholder: t('date-range-duration-placeholder'),
  customRelativeRangeOptionLabel: t('date-range-custom-option-label'),
  customRelativeRangeOptionDescription: t(
    'date-range-custom-option-description',
  ),
  customRelativeRangeUnitLabel: t('date-range-unit-label'),
  // istanbul ignore next -- Cloudscape i18n callback; invoked internally by DateRangePicker
  formatRelativeRange: /* istanbul ignore next */ (range) =>
    t(RELATIVE_RANGE_MESSAGE_IDS[range.unit], { amount: range.amount }),
  // istanbul ignore next -- Cloudscape i18n callback; invoked internally by DateRangePicker
  formatUnit: /* istanbul ignore next */ (unit, value) =>
    t(UNIT_IDS[unit], { value }),
  dateTimeConstraintText: t('date-range-constraint'),
  relativeModeTitle: t('date-range-relative-mode'),
  absoluteModeTitle: t('date-range-absolute-mode'),
  relativeRangeSelectionHeading: t('date-range-relative-heading'),
  startDateLabel: t('date-range-start-date'),
  endDateLabel: t('date-range-end-date'),
  clearButtonLabel: t('date-range-clear'),
  cancelButtonLabel: t('date-range-cancel'),
  applyButtonLabel: t('date-range-apply'),
});

interface DateRangeFilterProps {
  value: DateRangePickerProps.Value | null;
  onChange: (value: DateRangePickerProps.Value | null) => void;
}

/**
 * Reusable date range filter with 1-year limit validation.
 * Encapsulates presets, i18n strings, and validation logic.
 */
const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation();

  return (
    <FormField label={t('track-date-range-label')}>
      <DateRangePicker
        value={value}
        // istanbul ignore next -- Cloudscape internal event; not triggerable in jsdom
        onChange={
          /* istanbul ignore next */ ({ detail }) => onChange(detail.value)
        }
        dateOnly={true}
        relativeOptions={RELATIVE_PRESETS}
        // Bound here: both take `t` first, and Cloudscape calls them with only its own argument.
        isValidRange={(range) => isValidRange(t, range)}
        i18nStrings={dateRangeI18nStrings(t)}
        dateDisabledReason={(date) => dateDisabledReason(t, date)}
        isDateEnabled={isDateEnabled}
        placeholder={t('track-date-range-placeholder')}
        rangeSelectorMode="default"
      />
    </FormField>
  );
};

export default DateRangeFilter;
